/* eslint-disable formatjs/no-literal-string-in-jsx */
import { useEffect, useState } from "react";
import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import useUserPreference from "../../actions/user/useUserPreference.ts";
import { UserPreferenceKey } from "../../../../common/api/decimal/methods/user.ts";
import useFractionPersistValue, {
  FractionPersistKey,
} from "../../../../common/core-ui/useFractionPersistValue.ts";
import TextInput from "../../../../common/core-ui/TextInput.tsx";
import { usePromotionActivate } from "../../actions/explore-promotion/usePromotionActivate.tsx";
import { usePromotionReturn } from "../../actions/explore-promotion/usePromotionReturn.tsx";
import styles from "./DebugDialog.module.scss";
import { clsx } from "clsx";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import { Checkbox } from "../../../../common/core-ui/RadioCheck.tsx";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import { useCoursesExtras } from "../../homeContext.ts";
import { Section } from "./Section.tsx";

function DebugButton(props: React.ComponentPropsWithoutRef<"button">) {
  return (
    <button
      {...props}
      className={clsx("vst-link", "vst-link-option-no-underline")}
    />
  );
}

function ResetPreferenceButton({
  preferenceKey,
  ...buttonProps
}: React.ComponentPropsWithoutRef<"button"> & {
  preferenceKey: UserPreferenceKey;
}) {
  const [_value, _update, reset] = useUserPreference(preferenceKey);

  return <DebugButton {...buttonProps} onClick={() => reset()} />;
}

function PromotionsTest() {
  const [vbid, setVbid] = useState("");
  const { activatePromotion } = usePromotionActivate(vbid);
  const { returnPromotion } = usePromotionReturn(vbid);
  return (
    <>
      <div className={styles.vbidFooter}>
        <TextInput
          labelText={"Promotion VBID"}
          type="text"
          value={vbid}
          onChange={(event) => setVbid(event.target.value)}
        />
        <BaseLinkOrButton
          className="vst-button-rectangle"
          onClick={() => {
            void activatePromotion();
          }}
        >
          Activate
        </BaseLinkOrButton>
        <BaseLinkOrButton
          className={clsx("vst-button-rectangle", "vst-button-variant-outline")}
          onClick={() => {
            void returnPromotion();
          }}
        >
          Deactivate
        </BaseLinkOrButton>
      </div>
    </>
  );
}

export default function DebugDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [_explorePromoVal, setExplorePromoVal] = useFractionPersistValue(
    FractionPersistKey.SubscriptionPopoverStatus
  );
  const brand = useBrandLoaderData();
  const [useVerbaCore, setUseVerbaCore] = useState<boolean>(
    Boolean(window.localStorage.getItem("launchTenantOverride")) ?? false
  );
  const [tenantId, setTenantId] = useState<string | null>(null);
  const showUseVerbaCoreToggle = !brand.VirtualStore?.companyId && tenantId;
  const coursesExtrasPromise = useCoursesExtras();

  useEffect(() => {
    coursesExtrasPromise
      .then((extras) => setTenantId(extras.tenantId ?? null))
      .catch(() => {});
  }, [coursesExtrasPromise]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.code === "KeyD" && e.altKey) {
        setIsOpen(true);
      }
    };

    window.addEventListener("keyup", listener);
    return () => {
      window.removeEventListener("keyup", listener);
    };
  }, []);

  if (isOpen) {
    return (
      <Dialog
        title="Debug Menu"
        darkHeader
        onRequestClose={() => setIsOpen(false)}
        dialogClassName={styles.dialog}
      >
        <p className={styles.tip}>
          Reset user preference items. In some cases you may need to reload the
          page to see changes.
        </p>
        <div className={styles.dialogContent}>
          <Section>
            <h2>Basic Banners</h2>
            <ResetPreferenceButton
              preferenceKey={
                UserPreferenceKey.MyLibraryOnboardingBannerDismissed
              }
            >
              My Library
            </ResetPreferenceButton>
            <h3>Dashboard</h3>
            <ResetPreferenceButton
              preferenceKey={
                UserPreferenceKey.DashboardOnboardingBannerDismissed
              }
            >
              Default Onboarding
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.DashboardNewNavBannerDismissed}
            >
              New Navigation
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={
                UserPreferenceKey.VirtualStoreServiceAnnouncementDismissInfo
              }
            >
              Virtual Store Service Announcement
            </ResetPreferenceButton>
          </Section>
          <Section>
            <h2>Expired</h2>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.ExpiredOnboardingBannerDismissed}
            >
              Banner Dismissed
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.LastSeenExpiredAt}
            >
              Page Viewed
            </ResetPreferenceButton>
          </Section>
          <Section>
            <h2>My Courses</h2>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.MyCoursesUserSurveyDismissedDate}
            >
              Survey Toast
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={
                UserPreferenceKey.MyCoursesVerifyShippingAddressExpiredOn
              }
            >
              Verify Shipping Address
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={
                UserPreferenceKey.MyCoursesAddressConfirmationExpiredOn
              }
            >
              Address Confirmation
            </ResetPreferenceButton>
            {showUseVerbaCoreToggle && (
              <Checkbox
                checked={useVerbaCore}
                onChange={(e) => {
                  setUseVerbaCore(e.target.checked);
                  localStorage.setItem(
                    "launchTenantOverride",
                    e.target.checked ? tenantId : ""
                  );
                  window.location.reload();
                }}
              >
                Use Verba Core {useVerbaCore ? `Tenant Id - ${tenantId}` : ""}
              </Checkbox>
            )}
          </Section>
          <Section>
            <h2>Faculty</h2>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.FacultySurveyDismissedDate}
            >
              Survey Toast
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.FacultyWelcomeModalState}
            >
              Welcome Modal
            </ResetPreferenceButton>
            <h3>Recommendations</h3>
            <ResetPreferenceButton
              preferenceKey={
                UserPreferenceKey.DashboardRecommendationsBannerDismissed
              }
            >
              Promo Banner Dismissed
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.LastSeenRecommendations}
            >
              Page Viewed
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.RecommendationsLive}
            >
              Feature Available
            </ResetPreferenceButton>
            <h3>Sample Search</h3>
            <ResetPreferenceButton
              preferenceKey={
                UserPreferenceKey.DashboardSamplesSearchBannerDismissed
              }
            >
              Promo Banner Dismissed
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.LastSeenSampleSearch}
            >
              Page Viewed
            </ResetPreferenceButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.SampleSearchLive}
            >
              Feature Available
            </ResetPreferenceButton>
          </Section>
          <Section>
            <h2>Other</h2>
            <DebugButton
              onClick={() => {
                setExplorePromoVal(null);
              }}
            >
              Explore Promo Box
            </DebugButton>
            <ResetPreferenceButton
              preferenceKey={UserPreferenceKey.HomeSearchHistory}
            >
              Search History
            </ResetPreferenceButton>
          </Section>
        </div>
        <PromotionsTest />
      </Dialog>
    );
  }
}
